"use client";

import React, { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
  Link,
} from "react-router-dom";
import axios from "axios";
import {
  FiChevronDown,
  FiHome,
  FiUsers,
  FiUser,
  FiEdit,
  FiBriefcase,
  FiTool,
  FiFolderPlus,
  FiDollarSign,
  FiFileText,
  FiTable,
  FiClock,
  FiCreditCard,
  FiSettings,
  FiCoffee,
  FiCodesandbox,
  FiClipboard,
} from "react-icons/fi";
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  TransitionChild,
} from "@headlessui/react";
import {
  Bars3Icon,
  BellIcon,
  XMarkIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";
import { BsArrowUpCircleFill, BsFillCartPlusFill } from "react-icons/bs";
import { FaClipboardQuestion } from "react-icons/fa6";
import { FaExchangeAlt } from "react-icons/fa";
import SignIn from "./SignIn";
import QuickBooksSuccess from "./components/QuickBooksSuccess";
import QuickBooksError from "./components/QuickBooksError";
import UserManagement from "./components/UserManagement";
import UserProfile from "./components/UserProfile";
import Leases from "./components/Leases"; // or wherever you saved it
import ProjectView from "./components/ProjectView";
import BuildiumProperties from "./components/Properties";
import {
  msalInstance,
  loginRequest,
  initializeMsal,
  handleRedirect,
  getAccessToken,
  reauthenticateWithBackend,
} from "./authConfig";

import Callback from "./components/Callback";

import logo from "./assets/rent-doc.png";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

// Axios interceptor for token refresh and tenant ID
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      try {
        const newToken = await reauthenticateWithBackend();
        if (newToken) {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          const tenantId = localStorage.getItem("tenantId");
          if (tenantId) {
            originalRequest.headers["X-Tenant-ID"] = tenantId; // Add tenant ID to headers
          }
          return axios(originalRequest);
        }
        console.warn("Interceptor: No new token, redirecting to /login");
        localStorage.removeItem("isLoggedIn");
        localStorage.removeItem("token");
        localStorage.removeItem("tenantId");
        window.location.href = "/login";
      } catch (err) {
        console.error("Interceptor: Token refresh failed:", err);
        localStorage.removeItem("isLoggedIn");
        localStorage.removeItem("token");
        localStorage.removeItem("tenantId");
        window.location.href = "/login";
      }
    }
    return Promise.reject(error);
  },
);

export default function App() {
  return (
    <Router>
      <MainApp />
    </Router>
  );
}

function MainApp() {
  const [user, setUser] = useState(null);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [desktopSidebarOpen, setDesktopSidebarOpen] = useState(
    localStorage.getItem("desktopSidebarOpen") === "true",
  );
  const [msalInitialized, setMsalInitialized] = useState(false);
  const [isAuthChecked, setIsAuthChecked] = useState(false);
  const [companyProfile, setCompanyProfile] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [activeNotificationTab, setActiveNotificationTab] = useState("All");
  const [isPageRefreshed, setIsPageRefreshed] = useState(true);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [tenantId, setTenantId] = useState(localStorage.getItem("tenantId"));
  const location = useLocation();
  const navigate = useNavigate();

  // Initialize MSAL
  useEffect(() => {
    async function setupMsal() {
      try {
        await initializeMsal();
        setMsalInitialized(true);
        if (location.pathname !== "/callback") {
          if (localStorage.getItem("isLoggedIn") === "true") {
            await checkUserSession();
          } else {
            setIsRedirecting(true);
            navigate("/login");
            setIsRedirecting(false);
          }
        }
      } catch (error) {
        console.error("MSAL setup failed:", error);
        setIsRedirecting(true);
        navigate("/login");
        setIsRedirecting(false);
      } finally {
        setIsAuthChecked(true);
      }
    }
    setupMsal();
  }, [navigate, location.pathname]);

  // Check user session with backend JWT
  async function checkUserSession() {
    try {
      const token = localStorage.getItem("token");
      const authProvider = localStorage.getItem("authProvider") || "microsoft";

      if (!token) {
        console.warn("No token found, redirecting to /login");
        setUser(null);
        localStorage.removeItem("userRole");
        localStorage.removeItem("isLoggedIn");
        localStorage.removeItem("token");
        localStorage.removeItem("tenantId");
        setIsRedirecting(true);
        navigate("/login");
        setIsRedirecting(false);
        return;
      }

      const tenantId = localStorage.getItem("tenantId");

      // Build headers — tenantId optional for Google users
      const headers = { Authorization: `Bearer ${token}` };
      if (tenantId) headers["X-Tenant-ID"] = tenantId;

      const [userResponse, companyResponse] = await Promise.all([
        axios.get("https://rentdoc-a0d223e1499e.herokuapp.com/api/auth/me", {
          headers,
        }),
        axios.get(
          "https://rentdoc-a0d223e1499e.herokuapp.com/api/company-profile",
          { headers },
        ),
      ]);

      if (userResponse.data.success) {
        setUser(userResponse.data.user);
        localStorage.setItem("userRole", userResponse.data.user.userRole);
        setCompanyProfile(companyResponse.data);
        if (tenantId) setTenantId(tenantId);
      } else {
        throw new Error("Invalid session");
      }
    } catch (error) {
      console.error("Session check failed:", error);
      setUser(null);
      setCompanyProfile(null);
      setTenantId(null);
      localStorage.removeItem("isLoggedIn");
      localStorage.removeItem("token");
      localStorage.removeItem("userRole");
      localStorage.removeItem("tenantId");
      setIsRedirecting(true);
      navigate("/login");
      setIsRedirecting(false);
    }
  }

  // Save current route
  useEffect(() => {
    if (
      !user ||
      location.pathname === "/login" ||
      location.pathname === "/callback" ||
      location.pathname === "/" ||
      isRedirecting
    ) {
      return;
    }

    try {
      const routeData = {
        pathname: location.pathname,
        state: location.state || {},
      };
      localStorage.setItem("lastVisitedRoute", JSON.stringify(routeData));
    } catch (error) {
      console.error("Error saving route:", error);
    }
  }, [location.pathname, location.state, user, isRedirecting]);

  // Restore route after MSAL initialization
  useEffect(() => {
    if (!msalInitialized || !isPageRefreshed) {
      return;
    }

    const validRoutes = [
      "/Projects",
      "/Projects/:id",
      "/Leads",
      "/Leads/:id",
      "/Opportunities",
      "/Opportunities/:opportunityId",
      "/contacts",
      "/condition",
      "/accounts",
      "/timesheets",
      "/pieceworkerlog",
      "/finances",
      "/bills",
      "/labour-items",
      "/users",
      "/payroll",
      "/Prospects",
    ];

    const handler = setTimeout(() => {
      try {
        const savedRoute = localStorage.getItem("lastVisitedRoute");

        if (savedRoute) {
          const { pathname, state } = JSON.parse(savedRoute);
          const isValidRoute = validRoutes.some((route) =>
            route.includes(":id")
              ? pathname.match(route.replace(":id", "[^/]+"))
              : route === pathname,
          );

          if (
            isValidRoute &&
            pathname !== "/login" &&
            pathname !== "/callback" &&
            pathname !== "/" &&
            pathname !== location.pathname
          ) {
            navigate(pathname, { state, replace: true });
          } else {
            if (location.pathname === "/" || location.pathname === "/login") {
              navigate("/leases", { replace: true });
            }
          }
        } else {
          if (location.pathname === "/" || location.pathname === "/login") {
            navigate("/leases", { replace: true });
          }
        }
      } catch (error) {
        console.error("App.js: Error restoring route:", error);
        navigate("/leases", { replace: true });
      } finally {
        setIsPageRefreshed(false);
      }
    }, 2000);

    return () => clearTimeout(handler);
  }, [msalInitialized, isPageRefreshed, navigate, location.pathname]);

  // Detect page refresh
  useEffect(() => {
    const handleLoad = () => {
      setIsPageRefreshed(true);
    };
    window.addEventListener("load", handleLoad);
    return () => window.removeEventListener("load", handleLoad);
  }, []);

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // Persist sidebar state
  useEffect(() => {
    localStorage.setItem("desktopSidebarOpen", desktopSidebarOpen);
  }, [desktopSidebarOpen]);

  // Fetch notifications
  useEffect(() => {
    if (user && tenantId) {
      const token = localStorage.getItem("token");
      axios
        .get(
          "https://rentdoc-a0d223e1499e.herokuapp.com/api/users/notifications",
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
              "X-User-ID": user.id,
              "X-Tenant-ID": tenantId,
            },
          },
        )
        .then((res) => {
          setNotifications(res.data.notifications || []);
        })
        .catch((err) => console.error("Error fetching notifications:", err));
    }
  }, [user, tenantId]);

  // Handle login
  const handleLogin = async (userData) => {
    setUser(userData);
    localStorage.setItem("isLoggedIn", "true");
    try {
      const token = localStorage.getItem("token");
      const tenantId = localStorage.getItem("tenantId");

      if (!token) {
        console.warn("No token found after login, redirecting to /login");
        setIsRedirecting(true);
        navigate("/login");
        setIsRedirecting(false);
        return;
      }

      // Build headers — tenantId optional for Google users
      const headers = { Authorization: `Bearer ${token}` };
      if (tenantId) headers["X-Tenant-ID"] = tenantId;

      const response = await axios.get(
        "https://rentdoc-a0d223e1499e.herokuapp.com/api/company-profile",
        { headers },
      );

      setCompanyProfile(response.data);
      if (tenantId) setTenantId(tenantId);
    } catch (err) {
      console.error("Error fetching company profile:", err);
      setIsRedirecting(true);
      navigate("/login");
      setIsRedirecting(false);
    }
  };

  // Handle logout
  const handleLogout = () => {
    const authProvider = localStorage.getItem("authProvider") || "microsoft";

    setUser(null);
    setCompanyProfile(null);
    setTenantId(null);
    localStorage.removeItem("isLoggedIn");
    localStorage.removeItem("token");
    localStorage.removeItem("tenantId");
    localStorage.removeItem("authProvider");
    localStorage.removeItem("givenName");
    localStorage.removeItem("surname");
    localStorage.removeItem("userRole");
    localStorage.removeItem("lastVisitedRoute");

    if (authProvider === "microsoft") {
      msalInstance.setActiveAccount(null);
      msalInstance
        .logoutRedirect()
        .catch((e) => console.error("Logout error:", e));
    } else {
      navigate("/login");
    }
  };

  // Mark notification as read
  const markAsRead = async (id) => {
    try {
      const token = localStorage.getItem("token");
      const tenantId = localStorage.getItem("tenantId");
      if (!token || !tenantId) {
        console.warn("No token or tenantId for marking notification, skipping");
        return;
      }

      await axios.put(
        `https://rentdoc-a0d223e1499e.herokuapp.com/api/users/notifications/${id}/read`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "X-User-ID": user.id,
            "X-Tenant-ID": tenantId,
          },
        },
      );

      setNotifications((prev) =>
        prev.map((n) =>
          n._id === id ? { ...n, read: true, readAt: new Date() } : n,
        ),
      );
    } catch (err) {
      console.error("Error marking notification as read:", err);
    }
  };

  // Handle notification click
  const handleNotificationClick = (notification) => {
    if (!notification.clickable) {
      markAsRead(notification._id);
      return;
    }

    markAsRead(notification._id);
    const { category, entityType, entityId, projectId } = notification;

    if (!projectId) {
      navigate("/Projects");
      return;
    }

    let route = `/Projects/${projectId._id}`;
    let state = { initialTab: "General" };

    switch (category) {
      case "materials":
        route = `/projects/${projectId._id}/material-order/${entityId}`;
        break;
      case "rfi":
        state = { initialTab: "Request for Information (RFI)" };
        break;
      case "change_orders":
        route = `/projects/${projectId._id}/change-orders/${entityId}`;
        break;
      case "time_and_materials":
        state = { initialTab: "Time & Material" };
        break;
      default:
        break;
    }

    navigate(route, { state });
  };

  // Get notification icon
  const getNotificationIcon = (category, read) => {
    const iconClass = classNames(
      read ? "text-gray-400" : "text-orange-400",
      "h-5 w-5 shrink-0",
    );
    switch (category) {
      case "materials":
        return <BsFillCartPlusFill className={iconClass} />;
      case "rfis":
        return <FaClipboardQuestion className={iconClass} />;
      case "change_orders":
        return <FaExchangeAlt className={iconClass} />;
      case "time_and_materials":
      case "other":
        return <BsArrowUpCircleFill className={iconClass} />;
      default:
        return <BsArrowUpCircleFill className={iconClass} />;
    }
  };

  const unreadCount = notifications.filter((n) => !n.read).length;
  const filteredNotifications =
    activeNotificationTab === "Unread"
      ? notifications.filter((n) => !n.read)
      : notifications;

  const renderContent = () => {
    if (location.pathname === "/callback") {
      return <Callback onLogin={handleLogin} />;
    }

    if (!msalInitialized || !isAuthChecked) {
      return <div>Loading...</div>;
    }

    // AFTER
    if (!user) {
      return <SignIn onLogin={handleLogin} />;
    }

    return (
      <div className="bg-gray-100 md:min-h-[2000px] min-w-[1400px] ">
        <Dialog
          open={mobileSidebarOpen}
          onClose={setMobileSidebarOpen}
          className="relative z-50 lg:hidden"
        >
          <DialogBackdrop
            transition
            className="fixed inset-0 bg-gray-900/80 transition-opacity duration-300 ease-linear data-closed:opacity-0"
          />
          <div className="fixed inset-0 flex">
            <DialogPanel
              transition
              className="relative mr-16 flex w-full max-w-xs flex-1 transform transition duration-300 ease-in-out data-closed:-translate-x-full"
            >
              <TransitionChild>
                <div className="absolute top-0 left-full flex w-16 justify-center pt-5 duration-300 ease-in-out data-closed:opacity-0">
                  <button
                    type="button"
                    onClick={() => setMobileSidebarOpen(false)}
                    className="-m-2.5 p-2.5"
                  >
                    <span className="sr-only">Close sidebar</span>
                    <XMarkIcon
                      aria-hidden="true"
                      className="size-6 text-white"
                    />
                  </button>
                </div>
              </TransitionChild>
              <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-gray-700 px-6 pb-4">
                <div className="flex h-16 shrink-0 items-center">
                  <img
                    alt="Your Company"
                    src={
                      companyProfile?.logo || "https://via.placeholder.com/32"
                    }
                    className="h-8 w-auto"
                  />
                </div>
                <nav className="flex flex-1 flex-col">
                  <SidebarItems user={user} location={location} open={true} />
                </nav>
              </div>
            </DialogPanel>
          </div>
        </Dialog>

        <div
          className={`hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:flex-col ${
            desktopSidebarOpen ? "lg:w-72" : "lg:w-20"
          }`}
        >
          <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-gray-700 px-6 pb-4">
            <div
              className={`flex h-16 shrink-0 -ml-4 ${
                desktopSidebarOpen ? "-ml-4" : "-mr-4"
              }`}
            >
              <img
                alt="Your Company"
                src={logo}
                className={`w-auto ${
                  desktopSidebarOpen ? "lg:h-20" : "lg:h-16"
                }`}
              />
            </div>
            <nav className="flex flex-1 flex-col">
              <SidebarItems
                user={user}
                location={location}
                open={desktopSidebarOpen}
              />
              <ul role="list" className="flex flex-1 flex-col gap-y-7">
                <li className="mt-auto">
                  <button
                    onClick={() => setDesktopSidebarOpen((prev) => !prev)}
                    className="group -mx-2 flex gap-x-3 rounded-md p-2 text-sm/6 font-semibold text-gray-300 hover:bg-gray-600 hover:text-white"
                  >
                    {desktopSidebarOpen ? (
                      <ChevronLeftIcon
                        aria-hidden="true"
                        className="size-6 shrink-0 text-gray-300 group-hover:text-white"
                      />
                    ) : (
                      <ChevronRightIcon
                        aria-hidden="true"
                        className="size-6 shrink-0 text-gray-300 group-hover:text-white"
                      />
                    )}
                    {desktopSidebarOpen && "Hide"}
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        </div>

        <div>
          <div className="sticky top-0 z-[10] flex h-16 shrink-0 items-center gap-x-4 border-b border-gray-200 bg-white px-4 shadow-xs sm:gap-x-6 sm:px-6 lg:px-8">
            <button
              type="button"
              onClick={() => setMobileSidebarOpen(true)}
              className="-m-2.5 p-2.5 text-gray-700 lg:hidden"
            >
              <span className="sr-only">Open sidebar</span>
              <Bars3Icon aria-hidden="true" className="size-6" />
            </button>

            <div
              aria-hidden="true"
              className="h-6 w-px bg-gray-900/10 lg:hidden"
            />

            <div className="flex flex-1 gap-x-4 self-stretch lg:gap-x-6">
              <div className="flex flex-1"></div>
              <div className="flex items-center gap-x-4 lg:gap-x-6">
                <Menu as="div" className="relative">
                  <MenuButton className="-m-2.5 p-2.5 text-gray-400 hover:text-gray-500">
                    <span className="sr-only">View notifications</span>
                    <div className="relative">
                      <BellIcon aria-hidden="true" className="size-6" />
                      {unreadCount > 0 && (
                        <span className="absolute -top-1 -right-1 inline-flex h-5 w-5 items-center justify-center rounded-full bg-red-600 text-xs font-bold text-white">
                          {unreadCount}
                        </span>
                      )}
                    </div>
                  </MenuButton>
                  <MenuItems
                    transition
                    className="absolute right-0 z-10 mt-2 w-96 origin-top-right rounded-xl bg-gray-700 py-2 shadow-xl ring-1 ring-gray-600 focus:outline-none transition data-[closed]:scale-95 data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
                  >
                    <div className="flex border-b border-gray-600 mb-2">
                      <button
                        onClick={() => setActiveNotificationTab("All")}
                        className={classNames(
                          activeNotificationTab === "All"
                            ? "border-b-2 border-blue-400 text-blue-400"
                            : "text-gray-400 hover:text-white hover:bg-gray-600",
                          "flex-1 py-1.5 px-4 text-sm font-semibold text-center inline-block transition-colors duration-200",
                        )}
                      >
                        All
                      </button>
                      <button
                        onClick={() => setActiveNotificationTab("Unread")}
                        className={classNames(
                          activeNotificationTab === "Unread"
                            ? "border-b-2 border-blue-400 text-blue-400"
                            : "text-gray-400 hover:text-white hover:bg-gray-600",
                          "flex-1 py-1.5 px-4 text-sm font-semibold text-center inline-block transition-colors duration-200",
                        )}
                      >
                        Unread
                      </button>
                    </div>
                    <div className="max-h-80 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-500 scrollbar-track-gray-700 scrollbar-thumb-rounded">
                      {filteredNotifications.length === 0 ? (
                        <div className="px-4 py-3 text-sm text-gray-400 text-center">
                          No {activeNotificationTab.toLowerCase()} notifications
                        </div>
                      ) : (
                        filteredNotifications.map((notif) => (
                          <MenuItem key={notif._id}>
                            <div
                              onClick={() => handleNotificationClick(notif)}
                              className={classNames(
                                notif.read
                                  ? "bg-gray-700 text-gray-400"
                                  : "bg-gray-600 text-gray-200",
                                notif.clickable
                                  ? "hover:bg-gray-600 cursor-pointer"
                                  : "cursor-default",
                                "flex items-center gap-x-3 px-4 py-3 text-sm border-b border-gray-600 last:border-b-0 transition-colors duration-150",
                              )}
                            >
                              {getNotificationIcon(notif.category, notif.read)}
                              <div className="flex-1">
                                <p className="font-medium truncate">
                                  {notif.category === "materials" &&
                                  notif.materialOrderNumber
                                    ? `${notif.title} - #${notif.materialOrderNumber}`
                                    : notif.category === "rfi" &&
                                        notif.rfiNumber
                                      ? `${notif.title} - #${notif.rfiNumber}`
                                      : notif.category ===
                                            "time_and_materials" &&
                                          notif.timeAndMaterialNumber
                                        ? `${notif.title} - #${notif.timeAndMaterialNumber}`
                                        : notif.category === "change_orders" &&
                                            notif.changeOrderNumber
                                          ? `${notif.title} - #${notif.changeOrderNumber}`
                                          : notif.title}
                                </p>
                                {notif.projectName && (
                                  <p className="text-xs text-gray-500 mt-1">
                                    {notif.projectName}
                                  </p>
                                )}
                                <p className="text-xs text-orange-400 mt-1">
                                  {formatRelativeTime(notif.createdAt)}
                                </p>
                              </div>
                            </div>
                          </MenuItem>
                        ))
                      )}
                    </div>
                  </MenuItems>
                </Menu>

                <div
                  aria-hidden="true"
                  className="hidden lg:block lg:h-6 lg:w-px lg:bg-gray-900/10"
                />

                <Menu as="div" className="relative">
                  <MenuButton className="relative flex items-center">
                    <span className="absolute -inset-1.5" />
                    <span className="sr-only">Open user menu</span>
                    <img
                      alt=""
                      src={
                        companyProfile?.logo || "https://via.placeholder.com/32"
                      }
                      className="size-12 bg-black rounded-full py-1"
                    />
                    <span className="hidden lg:flex lg:items-center">
                      <span
                        aria-hidden="true"
                        className="ml-4 text-sm/6 font-semibold text-gray-900"
                      >
                        {user?.displayName}
                      </span>
                      <ChevronDownIcon
                        aria-hidden="true"
                        className="ml-2 size-5 text-gray-400"
                      />
                    </span>
                  </MenuButton>
                  <MenuItems
                    transition
                    className="absolute right-0 z-10 mt-2.5 w-32 origin-top-right rounded-md bg-white py-2 shadow-lg ring-1 ring-gray-900/5 transition focus:outline-hidden data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-in"
                  >
                    {userNavigation(user, handleLogout).map((item) => (
                      <MenuItem key={item.name}>
                        <Link
                          to={item.href}
                          onClick={
                            item.onClick
                              ? (e) => {
                                  e.preventDefault();
                                  item.onClick();
                                }
                              : undefined
                          }
                          className={classNames(
                            item.name === "Sign out"
                              ? "text-red-600"
                              : "text-gray-900",
                            "block px-3 py-1 text-sm/6 data-focus:bg-gray-50 data-focus:outline-hidden",
                          )}
                        >
                          {item.name}
                        </Link>
                      </MenuItem>
                    ))}
                  </MenuItems>
                </Menu>
              </div>
            </div>
          </div>

          <main
            className={`${desktopSidebarOpen ? "ml-56 px-12" : "px-8 ml-8"} `}
          >
            <div className="px-4 sm:px-6 lg:px-8 ">
              {user?.role === "unassigned" ? (
                <h1 className="text-2xl">
                  Welcome {user.displayName}! Your role is unassigned. Please
                  contact an admin.
                </h1>
              ) : (
                <Routes>
                  <Route path="/" element={<Navigate to="/leases" />} />
                  <Route path="/users/:userId" element={<UserProfile />} />
                  <Route path="/properties" element={<BuildiumProperties />} />
                  <Route path="/leases" element={<Leases />} />
                  <Route
                    path="/users"
                    element={
                      user.role === "admin" ? (
                        <UserManagement />
                      ) : (
                        <Navigate to="/leases" />
                      )
                    }
                  />
                  <Route
                    path="/quickbooks-success"
                    element={<QuickBooksSuccess />}
                  />
                  <Route
                    path="/quickbooks-error"
                    element={<QuickBooksError />}
                  />
                </Routes>
              )}
            </div>
          </main>
        </div>
      </div>
    );
  };

  // Format relative time for notifications
  const formatRelativeTime = (date) => {
    const now = new Date();
    const diffInMs = now - new Date(date);
    const seconds = Math.floor(diffInMs / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) return `${days}d`;
    if (hours > 0) return `${hours}h`;
    if (minutes > 0) return `${minutes}m`;
    return "Just now";
  };

  return renderContent();
}

function SidebarItems({ user, location, open }) {
  const [timezone, setTimezone] = useState(
    localStorage.getItem("userTimezone") || "UTC",
  );
  const timezones = [
    "UTC",
    "America/Toronto",
    "America/Chicago",
    "America/Denver",
    "America/Los_Angeles",
    "Europe/London",
    "Asia/Tokyo",
    "Australia/Sydney",
  ];

  const handleTimezoneChange = (e) => {
    const newTimezone = e.target.value;
    setTimezone(newTimezone);
    localStorage.setItem("userTimezone", newTimezone);
  };

  const dataItems = [
    { name: "Buildium Leases", href: "/leases", icon: FiClipboard },
    { name: "Buildium Properties", href: "/properties", icon: FiHome },
    // ... any other data-related items you have (Projects, Leads, etc.)
  ];

  const systemItems = [{ name: "Settings", href: "/users", icon: FiSettings }];

  const computedSystem = systemItems.map((item) => ({
    ...item,
    current: location.pathname.startsWith(item.href),
  }));

  return (
    <ul role="list" className="flex flex-1 flex-col gap-y-7 mt-2">
      <li>
        <ul role="list" className="-mx-2 space-y-1">
          {dataItems.map((item) => (
            <li key={item.name}>
              <Link
                to={item.href}
                className={classNames(
                  location.pathname === item.href
                    ? "bg-gray-800 text-white"
                    : "text-gray-400 hover:bg-gray-800 hover:text-white",
                  "group flex gap-x-3 rounded-md p-2 text-sm/6 font-semibold",
                )}
              >
                <item.icon aria-hidden="true" className="size-6 shrink-0" />
                {open && item.name}
              </Link>
            </li>
          ))}
        </ul>
      </li>

      {/* System Section (hidden for foreman) */}
      {user?.role !== "foreman" && (
        <li>
          {open && (
            <div className="relative">
              <div
                aria-hidden="true"
                className="absolute inset-0 flex items-center"
              >
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center">
                <span className="px-3 text-sm font-semibold text-white bg-gray-700">
                  System
                </span>
              </div>
            </div>
          )}
          {user?.role === "admin" && (
            <ul role="list" className="-mx-2 mt-2 space-y-1">
              {computedSystem.map((item) => (
                <li key={item.name}>
                  <Link
                    to={item.href}
                    className={classNames(
                      item.current
                        ? "bg-orange-600 text-white"
                        : "text-gray-300 hover:text-white hover:bg-gray-600",
                      "group flex gap-x-3 rounded-md p-2 text-sm/6 font-semibold",
                    )}
                  >
                    <item.icon
                      className={classNames(
                        item.current
                          ? "text-white"
                          : "text-gray-300 group-hover:text-white",
                        "size-6 shrink-0",
                      )}
                      aria-hidden="true"
                    />
                    {open && item.name}
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </li>
      )}
    </ul>
  );
}

function userNavigation(user, handleLogout) {
  const navigationItems = [
    { name: "Sign out", href: "#", onClick: handleLogout },
  ];

  if (user?.role === "admin") {
    navigationItems.unshift({ name: "Settings", href: `/users/` });
  }

  return navigationItems;
}
